import React, { useState, useEffect, useContext } from 'react'
import { Container, Button, Row, Col, Card, Modal } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  getnumofRcptsOwner,
  getOwnerUniqueCustomers,
  getOwnerDashboardStats
} from '../actions/dashboardActions.js'
import { ThemeContext } from '../components/ThemeContext.js'

const OwnerDashBoard = () => {
  const { theme } = useContext(ThemeContext)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showWelcome, setShowWelcome] = useState(false)

  const handleWelcomeClose = () => {
    if (ownerInfo)
      localStorage.setItem(`mr_owner_welcome_seen_${ownerInfo._id}`, '1')
    setShowWelcome(false)
  }

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const countRcpt = useSelector((state) => state.countRcpt)
  const { countRecpt } = countRcpt

  const ownerUniqueCustomersState = useSelector(
    (state) => state.ownerUniqueCustomers
  )
  const { uniqueCustomers } = ownerUniqueCustomersState

  const ownerDashboardStatsState = useSelector(
    (state) => state.ownerDashboardStats
  )
  const { ownerDashboardStats } = ownerDashboardStatsState

  const requiredProfileFields = [
    ownerInfo?.bname,
    ownerInfo?.phone,
    ownerInfo?.address,
    ownerInfo?.city,
    ownerInfo?.state,
    ownerInfo?.country,
    ownerInfo?.mailcode
  ]
  const missingProfileFields = requiredProfileFields.filter(
    (field) => !String(field || '').trim()
  ).length
  const hasCompleteBusinessProfile = missingProfileFields === 0

  const formatLastReceipt = (value) => {
    if (!value) return 'No receipts sent yet.'

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(new Date(value))
  }

  useEffect(() => {
    if (!ownerInfo) {
      navigate('/')
    } else {
      dispatch(getnumofRcptsOwner(ownerInfo.cid))
      dispatch(getOwnerUniqueCustomers(ownerInfo.cid))
      dispatch(getOwnerDashboardStats(ownerInfo.cid))
    }
  }, [dispatch, navigate, ownerInfo])

  // Show welcome modal only for new owners (no receipts issued yet), once per owner
  useEffect(() => {
    if (!ownerInfo) return
    const key = `mr_owner_welcome_seen_${ownerInfo._id}`
    if (localStorage.getItem(key)) return
    if (countRecpt === undefined) return
    if (countRecpt === 0) {
      setShowWelcome(true)
    } else {
      localStorage.setItem(key, '1')
    }
  }, [countRecpt, ownerInfo])

  return (
    <Container fluid>
      <Row>
        <Col className='my-2'>
          <h4 style={{ color: theme.text }}>DashBoard</h4>
        </Col>
      </Row>

      {/* Welcome modal — shown once for new owners */}
      <Modal show={showWelcome} onHide={handleWelcomeClose} centered>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: theme.card,
            borderColor: theme.sidebarAccent
          }}
        >
          <Modal.Title style={{ color: theme.accent }}>
            Welcome to MicroReceipt Business 👋
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme.card, color: theme.text }}>
          <p>Here's how to start issuing digital receipts:</p>
          <ol style={{ paddingLeft: '1.25rem', color: theme.subtext }}>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: theme.text }}>
                Verify your business profile
              </strong>{' '}
              — your business name, address, and contact details are printed on
              every receipt image sent to customers. Make sure they are correct
              before issuing any receipts.
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: theme.text }}>Integrate the API</strong> —
              use your API key to send receipts directly to customers at
              checkout.
            </li>
            <li>
              <strong style={{ color: theme.text }}>Track performance</strong> —
              monitor issued receipts and daily totals from this dashboard.
            </li>
          </ol>
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: theme.card,
            borderColor: theme.sidebarAccent
          }}
        >
          <Button
            size='sm'
            onClick={() => {
              handleWelcomeClose()
              navigate('/ownerprofile')
            }}
            style={{
              borderColor: theme.accent,
              color: theme.accent,
              backgroundColor: 'transparent',
              border: `1px solid ${theme.accent}`,
              marginRight: '0.5rem'
            }}
          >
            My Profile
          </Button>
          <Button
            size='sm'
            onClick={() => {
              handleWelcomeClose()
              navigate('/apidocument')
            }}
            style={{
              borderColor: theme.accent,
              color: theme.accent,
              backgroundColor: 'transparent',
              border: `1px solid ${theme.accent}`
            }}
          >
            View API Docs
          </Button>
          <Button size='sm' variant='secondary' onClick={handleWelcomeClose}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Zero-state get-started banner */}
      {countRecpt === 0 && (
        <Row className='mb-2'>
          <Col>
            <Card
              style={{
                backgroundColor: theme.card,
                borderColor: theme.accent,
                borderRadius: '8px'
              }}
            >
              <Card.Body>
                <Card.Title style={{ color: theme.accent }}>
                  Get started
                </Card.Title>
                <Card.Text style={{ color: theme.subtext }}>
                  No receipts issued yet. Integrate the API and start sending
                  digital receipts to your customers at checkout.
                </Card.Text>
                <Button
                  size='sm'
                  onClick={() => navigate('/apidocument')}
                  style={{
                    borderColor: theme.accent,
                    color: theme.accent,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.accent}`,
                    marginRight: '0.5rem'
                  }}
                >
                  API Docs
                </Button>
                <Button
                  size='sm'
                  onClick={() => navigate('/ownerprofile')}
                  style={{
                    borderColor: theme.accent,
                    color: theme.accent,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.accent}`
                  }}
                >
                  My Profile
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '8px'
            }}
            className='mb-2'
          >
            <Card.Body>
              <Card.Title>Issued Receipts</Card.Title>
              <Card.Text>You have {countRecpt || 0} issued receipts.</Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent
              }}
            >
              <Button
                size='sm'
                variant='outline-primary'
                onClick={() => navigate('/receiptchart')}
                style={{
                  borderColor: theme.accent,
                  color: theme.accent,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.accent}`
                }}
              >
                Receipt Charts
              </Button>
            </Card.Footer>
          </Card>
        </Col>

        <Col>
          <Card
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '8px'
            }}
            className='mb-2'
          >
            <Card.Body>
              <Card.Title>Unique Customers</Card.Title>
              <Card.Text>
                You've sent receipts to {uniqueCustomers || 0} individual
                customers.
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent
              }}
            >
              <small>
                Based on distinct customer accounts linked to receipts.
              </small>
            </Card.Footer>
          </Card>
        </Col>

        <Col>
          <Card
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '8px'
            }}
            className='mb-2'
          >
            <Card.Body>
              <Card.Title>Receipts Today</Card.Title>
              <Card.Text>
                You've issued {ownerDashboardStats?.receiptsToday || 0} receipts
                today.
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent
              }}
            >
              <small>
                Based on receipts created since midnight server time.
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '8px'
            }}
            className='mb-2'
          >
            <Card.Body>
              <Card.Title>Business Details</Card.Title>
              <Card.Text>
                {hasCompleteBusinessProfile
                  ? 'Your receipt business details are complete.'
                  : `Your receipt profile is missing ${missingProfileFields} required field${missingProfileFields === 1 ? '' : 's'}.`}
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent
              }}
            >
              <Button
                size='sm'
                variant='outline-primary'
                onClick={() => navigate('/ownerprofile')}
                style={{
                  borderColor: theme.accent,
                  color: theme.accent,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.accent}`
                }}
              >
                Review Profile
              </Button>
            </Card.Footer>
          </Card>
        </Col>

        <Col>
          <Card
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '8px'
            }}
            className='mb-2'
          >
            <Card.Body>
              <Card.Title>Last Receipt Sent</Card.Title>
              <Card.Text>
                {formatLastReceipt(ownerDashboardStats?.lastReceiptAt)}
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent
              }}
            >
              <small>Most recent receipt issued from your account.</small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* API docs quick-link — always visible */}
      <Row>
        <Col>
          <Card
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '8px'
            }}
            className='mb-2'
          >
            <Card.Body>
              <Card.Title>API Integration</Card.Title>
              <Card.Text style={{ color: theme.subtext }}>
                Use the MicroReceipt API to send digital receipts to customers
                directly from your point-of-sale system.
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent
              }}
            >
              <Button
                size='sm'
                variant='outline-primary'
                onClick={() => navigate('/apidocument')}
                style={{
                  borderColor: theme.accent,
                  color: theme.accent,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.accent}`
                }}
              >
                API Docs
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default OwnerDashBoard
